import { Box, Text, HStack, VStack, Icon, Skeleton, Badge, useColorModeValue, Tooltip, Progress, Divider, Heading, SimpleGrid } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { FaCloud, FaCloudSun, FaSun, FaCloudRain, FaSnowflake, FaWind, FaTint, FaEye, FaThermometerHalf, FaMapMarkerAlt, FaSyncAlt, FaMoon } from 'react-icons/fa';
import api from '../../services/api';

interface WeatherData {
  location: {
    name: string;
    region: string;
    country: string;
    lat: number;
    lon: number;
    tz_id: string;
    localtime: string;
  };
  current: {
    last_updated: string;
    temp_c: number;
    temp_f: number;
    is_day: number;
    condition: {
      text: string;
      icon: string;
      code: number;
    };
    wind_mph: number;
    wind_kph: number;
    wind_degree: number;
    wind_dir: string;
    pressure_mb: number;
    pressure_in: number;
    precip_mm: number;
    precip_in: number;
    humidity: number;
    cloud: number;
    feelslike_c: number;
    feelslike_f: number;
    vis_km: number;
    vis_miles: number;
    uv: number;
    gust_mph: number;
    gust_kph: number;
  };
  forecast: {
    forecastday: Array<{
      date: string;
      date_epoch: number;
      day: {
        maxtemp_c: number;
        maxtemp_f: number;
        mintemp_c: number;
        mintemp_f: number;
        avgtemp_c: number;
        avgtemp_f: number;
        maxwind_mph: number;
        maxwind_kph: number;
        totalprecip_mm: number;
        totalprecip_in: number;
        totalsnow_cm: number;
        avgvis_km: number;
        avgvis_miles: number;
        avghumidity: number;
        daily_will_it_rain: number;
        daily_chance_of_rain: number;
        daily_will_it_snow: number;
        daily_chance_of_snow: number;
        condition: {
          text: string;
          icon: string;
          code: number;
        };
        uv: number;
      };
      astro: {
        sunrise: string;
        sunset: string;
        moonrise: string;
        moonset: string;
        moon_phase: string;
        moon_illumination: string;
        is_moon_up: number;
        is_sun_up: number;
      };
      hour: Array<{
        time_epoch: number;
        time: string;
        temp_c: number;
        temp_f: number;
        is_day: number;
        condition: {
          text: string;
          icon: string;
          code: number;
        };
        wind_mph: number;
        wind_kph: number;
        wind_degree: number;
        wind_dir: string;
        pressure_mb: number;
        pressure_in: number;
        precip_mm: number;
        precip_in: number;
        humidity: number;
        cloud: number;
        feelslike_c: number;
        feelslike_f: number;
        windchill_c: number;
        windchill_f: number;
        heatindex_c: number;
        heatindex_f: number;
        dewpoint_c: number;
        dewpoint_f: number;
        will_it_rain: number;
        chance_of_rain: number;
        will_it_snow: number;
        chance_of_snow: number;
        vis_km: number;
        vis_miles: number;
        gust_mph: number;
        gust_kph: number;
        uv: number;
      }>;
    }>;
  };
}

const getWeatherIcon = (condition: string, isDay: number) => {
  const lowerCondition = condition.toLowerCase();
  
  if (lowerCondition.includes('sunny') || lowerCondition.includes('clear')) {
    return isDay ? FaSun : FaMoon;
  }
  if (lowerCondition.includes('partly cloudy') || lowerCondition.includes('partly sunny')) {
    return FaCloudSun;
  }
  if (lowerCondition.includes('cloudy') || lowerCondition.includes('overcast')) {
    return FaCloud;
  }
  if (lowerCondition.includes('rain') || lowerCondition.includes('drizzle') || lowerCondition.includes('shower')) {
    return FaCloudRain;
  }
  if (lowerCondition.includes('snow') || lowerCondition.includes('sleet') || lowerCondition.includes('blizzard')) {
    return FaSnowflake;
  }
  
  return FaCloud;
};

const getUVColor = (uv: number) => {
  if (uv <= 2) return 'green';
  if (uv <= 5) return 'yellow';
  if (uv <= 7) return 'orange';
  if (uv <= 10) return 'red';
  return 'purple';
};

const getUVLabel = (uv: number) => {
  if (uv <= 2) return 'Nízké';
  if (uv <= 5) return 'Střední';
  if (uv <= 7) return 'Vysoké';
  if (uv <= 10) return 'Velmi vysoké';
  return 'Extrémní';
};

const getWindDirection = (degree: number) => {
  const directions = ['S', 'SV', 'V', 'JV', 'J', 'JZ', 'Z', 'SZ'];
  const index = Math.round(degree / 45) % 8;
  return directions[index];
};

const WeatherWidget = () => {
  const { data: weather, isLoading, error, refetch } = useQuery<WeatherData>({
    queryKey: ['weather', 'club'],
    queryFn: async () => {
      const response = await api.get('/weather/club');
      return response.data;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    refetchInterval: 15 * 60 * 1000, // 15 minutes
  });

  const bgCard = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.600', 'gray.400');

  if (isLoading) {
    return (
      <Box
        bg={bgCard}
        p={6}
        borderRadius="xl"
        boxShadow="md"
        borderWidth="1px"
        borderColor={borderColor}
      >
        <VStack spacing={4} align="stretch">
          <Skeleton height="20px" width="60%" />
          <Skeleton height="40px" width="80%" />
          <Skeleton height="16px" width="40%" />
          <Skeleton height="16px" width="50%" />
        </VStack>
      </Box>
    );
  }

  if (error || !weather) {
    return (
      <Box
        bg={bgCard}
        p={6}
        borderRadius="xl"
        boxShadow="md"
        borderWidth="1px"
        borderColor={borderColor}
      >
        <VStack spacing={4} align="center">
          <Icon as={FaCloud} boxSize={12} color="gray.400" />
          <Text color="gray.500" textAlign="center">
            Počasí není dostupné
          </Text>
          <Text fontSize="sm" color="gray.400" textAlign="center">
            Zkuste to znovu později
          </Text>
        </VStack>
      </Box>
    );
  }

  const currentIcon = getWeatherIcon(weather.current.condition.text, weather.current.is_day);
  const uvColor = getUVColor(weather.current.uv);
  const uvLabel = getUVLabel(weather.current.uv);

  return (
    <Box
      bg={bgCard}
      p={6}
      borderRadius="xl"
      boxShadow="md"
      borderWidth="1px"
      borderColor={borderColor}
      position="relative"
      overflow="hidden"
    >
      {/* Refresh button */}
      <Box position="absolute" top={4} right={4}>
        <Tooltip label="Obnovit počasí">
          <Box
            as="button"
            onClick={() => refetch()}
            p={2}
            borderRadius="md"
            _hover={{ bg: useColorModeValue('gray.100', 'gray.700') }}
            transition="all 0.2s"
          >
            <Icon as={FaSyncAlt} color={textColor} boxSize={4} />
          </Box>
        </Tooltip>
      </Box>

      <VStack spacing={4} align="stretch">
        {/* Location */}
        <HStack spacing={2} color={textColor}>
          <Icon as={FaMapMarkerAlt} boxSize={3} />
          <Text fontSize="sm" fontWeight="medium">
            {weather.location.name}
            {weather.location.region && `, ${weather.location.region}`}
          </Text>
        </HStack>

        {/* Current Weather */}
        <HStack spacing={4} align="center">
          <VStack spacing={1} align="center" minW="80px">
            <Icon as={currentIcon} boxSize={12} color="blue.400" />
            <Text fontSize="sm" color={textColor} textAlign="center">
              {weather.current.condition.text}
            </Text>
          </VStack>
          
          <VStack spacing={1} align="start" flex={1}>
            <Text fontSize="4xl" fontWeight="bold" lineHeight="1">
              {Math.round(weather.current.temp_c)}°C
            </Text>
            <Text fontSize="sm" color={textColor}>
              Pocitově {Math.round(weather.current.feelslike_c)}°C
            </Text>
          </VStack>
        </HStack>

        <Divider />

        {/* Weather Details Grid */}
        <SimpleGrid columns={2} spacing={3}>
          <HStack spacing={2}>
            <Icon as={FaWind} boxSize={4} color={textColor} />
            <VStack spacing={0} align="start">
              <Text fontSize="xs" color={textColor}>Vítr</Text>
              <Text fontSize="sm" fontWeight="medium">
                {weather.current.wind_kph} km/h {getWindDirection(weather.current.wind_degree)}
              </Text>
            </VStack>
          </HStack>

          <HStack spacing={2}>
            <Icon as={FaTint} boxSize={4} color={textColor} />
            <VStack spacing={0} align="start">
              <Text fontSize="xs" color={textColor}>Vlhkost</Text>
              <Text fontSize="sm" fontWeight="medium">{weather.current.humidity}%</Text>
            </VStack>
          </HStack>

          <HStack spacing={2}>
            <Icon as={FaEye} boxSize={4} color={textColor} />
            <VStack spacing={0} align="start">
              <Text fontSize="xs" color={textColor}>Viditelnost</Text>
              <Text fontSize="sm" fontWeight="medium">{weather.current.vis_km} km</Text>
            </VStack>
          </HStack>

          <HStack spacing={2}>
            <Icon as={FaThermometerHalf} boxSize={4} color={textColor} />
            <VStack spacing={0} align="start">
              <Text fontSize="xs" color={textColor}>Tlak</Text>
              <Text fontSize="sm" fontWeight="medium">{weather.current.pressure_mb} hPa</Text>
            </VStack>
          </HStack>
        </SimpleGrid>

        {/* UV Index */}
        <Box>
          <HStack justify="space-between" mb={1}>
            <Text fontSize="xs" color={textColor}>UV Index</Text>
            <Badge colorScheme={uvColor} fontSize="xs">
              {uvLabel} ({weather.current.uv})
            </Badge>
          </HStack>
          <Progress 
            value={Math.min(weather.current.uv * 10, 100)} 
            size="xs" 
            colorScheme={uvColor}
            borderRadius="md"
          />
        </Box>

        {/* Forecast for next 2 days */}
        {weather.forecast.forecastday.length > 1 && (
          <>
            <Divider />
            <Text fontSize="sm" fontWeight="bold" mb={2}>
              Předpověď na následující dny
            </Text>
            <SimpleGrid columns={2} spacing={3}>
              {weather.forecast.forecastday.slice(1, 3).map((day, index) => {
                const dayIcon = getWeatherIcon(day.day.condition.text, 1);
                const date = new Date(day.date);
                const dayName = date.toLocaleDateString('cs-CZ', { weekday: 'short' });
                
                return (
                  <Box
                    key={index}
                    p={3}
                    bg={useColorModeValue('gray.50', 'gray.700')}
                    borderRadius="md"
                    border="1px solid"
                    borderColor={useColorModeValue('gray.200', 'gray.600')}
                  >
                    <HStack spacing={3} align="center">
                      <Icon as={dayIcon} boxSize={6} color="blue.400" />
                      <VStack spacing={1} align="start" flex={1}>
                        <Text fontSize="xs" fontWeight="medium">{dayName}</Text>
                        <HStack spacing={2}>
                          <Text fontSize="sm" fontWeight="bold">
                            {Math.round(day.day.maxtemp_c)}°
                          </Text>
                          <Text fontSize="sm" color={textColor}>
                            {Math.round(day.day.mintemp_c)}°
                          </Text>
                        </HStack>
                        {day.day.daily_chance_of_rain > 0 && (
                          <Badge size="xs" colorScheme="blue">
                            {day.day.daily_chance_of_rain}% déšť
                          </Badge>
                        )}
                      </VStack>
                    </HStack>
                  </Box>
                );
              })}
            </SimpleGrid>
          </>
        )}

        {/* Last updated */}
        <Text fontSize="xs" color={textColor} textAlign="center">
          Aktualizováno: {new Date(weather.current.last_updated).toLocaleTimeString('cs-CZ', {
            hour: '2-digit',
            minute: '2-digit'
          })}
        </Text>
      </VStack>
    </Box>
  );
};

export default WeatherWidget;
